// The docs pages were authored dark-only with literal hex, so they stayed black
// when the app switched to light. This maps every literal they used to a
// light/dark pair. Dark values are the originals — dark mode is unchanged.
import { useTheme } from "@mui/material";

export const DARK = {
  ground: "#000",
  surface: "#060d1a",
  surfaceAlt: "#0a1020",
  border: "#1c1c1c",
  borderSoft: "#111",
  text: "#e2e8f0",
  textStrong: "#ffffff",
  textMuted: "#3a5070",
  textFaint: "#2a3a50",
  accent: "#3a8eff",
  accentHover: "#5fa8ff",
  onAccent: "#fff",
  success: "#22c55e",
  successAlt: "#4ade80",
  warning: "#eab308",
  hover: "rgba(255,255,255,0.05)",
  hoverSoft: "rgba(255,255,255,0.04)",
  scrollThumb: "rgba(255,255,255,0.1)",
  track: "rgba(255,255,255,0.06)",
  chipBg: "rgba(255,255,255,0.07)",
  chipBorder: "rgba(255,255,255,0.12)",
  chipText: "rgba(255,255,255,0.4)",
  placeholderIcon: "rgba(255,255,255,0.12)",
  skeleton: ["#1a1a1a", "#141414", "#111"],
};

export const LIGHT = {
  ground: "#FFFFFF",
  surface: "#F8FAFC",
  surfaceAlt: "#F1F5F9",
  border: "#E1E1E4",
  borderSoft: "#ECECEF",
  text: "#1A1A1A",
  textStrong: "#000000",
  textMuted: "#6E6E6E",
  // Light values are picked for >=4.5:1 on ground/surface — these carry small
  // 10-13px label text, so the obvious mid-greys don't clear AA.
  textFaint: "#767676",
  accent: "#006BCE",
  accentHover: "#0058A5",
  onAccent: "#fff",
  success: "#15803D",
  successAlt: "#22c55e",
  warning: "#A16207",
  hover: "rgba(0,0,0,0.05)",
  hoverSoft: "rgba(0,0,0,0.035)",
  scrollThumb: "rgba(0,0,0,0.18)",
  track: "rgba(0,0,0,0.06)",
  chipBg: "rgba(0,0,0,0.05)",
  chipBorder: "rgba(0,0,0,0.12)",
  chipText: "rgba(0,0,0,0.6)",
  placeholderIcon: "rgba(0,0,0,0.14)",
  skeleton: ["#E2E8F0", "#EDF1F5", "#F1F5F9"],
};

export const usePanelTokens = () =>
  useTheme().palette.mode === "dark" ? DARK : LIGHT;

// A key in one palette but not the other resolves to `undefined`, which CSS
// drops silently — the colour just goes missing in that one theme. Cheapest
// place to catch it is on load, since this repo has no working test runner.
if (process.env.NODE_ENV !== "production") {
  const extra = [
    ...Object.keys(DARK).filter((k) => !(k in LIGHT)),
    ...Object.keys(LIGHT).filter((k) => !(k in DARK)),
  ];
  if (extra.length) {
    console.error(`panelTokens: key(s) missing from one palette: ${extra.join(", ")}`);
  }
}
